// The live view's socket (D120, APS-547): a ticket from the API, then a WebSocket to the live
// Worker, for as long as the Live tab is open. Opening one writes the API's `live.view.start`
// audit row, so the tab opens it and nothing else does; leaving the tab closes it. The Worker
// closes it with 4001 at its ticket's deadline (30 minutes) unless the viewer renews it with a
// fresh ticket; other closes are retried a few times, then left to the viewer. A socket counts as
// working, and the retries start over, only once the Worker has answered a status or it has stayed
// open STEADY_MS: one it accepts and drops at once must not mint a ticket (an audit row) a second.
// Status is not pushed, so the view asks for it on open and every STATUS_EVERY_MS.
import { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import {
  initialView,
  type LiveFilter,
  type LiveProblem,
  type LiveView,
  liveReducer,
  RATE_LIMIT_WAIT_S,
  STATUS_EVERY_MS,
  TICKET_EXPIRED,
} from './live'

/** A ticket's answer: the URL to open (`wss://…/v1/live/view?ticket=…`) and the ticket itself. */
export interface LiveTicket {
  ticket: string
  url: string
}

/** Waits before each retry after a close that was not the deadline's. */
export const RETRY_MS = [1_000, 2_000, 4_000] as const
/** Open this long, a socket is working: its close starts the retries over. */
export const STEADY_MS = 30_000

export interface LiveViewHandle {
  view: LiveView
  /** Why the view is not open, when it could not open or gave up retrying. */
  problem: LiveProblem | null
  /** Why the last renew did not go out; the view itself stays as it was. */
  renewProblem: LiveProblem | null
  /** The Worker's own words when it refused a renew (its `error` reply). */
  renewRefused: string | null
  /** After a 429 (open or renew): Unix ms before which the viewer is not to try again. */
  retryAt: number | null
  /** Replaces the Worker's filter for this viewer. */
  setFilter: (filter: LiveFilter) => void
  /** A fresh ticket that moves the deadline and renews the tester's sessions. */
  renew: () => Promise<void>
  /** A new ticket and a new socket, after the deadline closed the view or retries gave up. */
  reconnect: () => void
  renewing: boolean
}

/**
 * The live view of one tester while `enabled`. `ticket` asks the API for a ticket; it rejects
 * with the problem. Only a URL on `origin` (the console's VITE_LIVE_URL) is opened.
 */
export function useLiveView(
  enabled: boolean,
  ticket: () => Promise<LiveTicket>,
  origin: string,
): LiveViewHandle {
  const [view, dispatch] = useReducer(liveReducer, initialView)
  const [problem, setProblem] = useState<LiveProblem | null>(null)
  const [renewProblem, setRenewProblem] = useState<LiveProblem | null>(null)
  const [renewRefused, setRenewRefused] = useState<string | null>(null)
  // A renew sent and not answered yet: an `error` reply then is the Worker refusing it.
  const renewSent = useRef(false)
  const [retryAt, setRetryAt] = useState<number | null>(null)
  const [renewing, setRenewing] = useState(false)
  const [generation, setGeneration] = useState(0)
  const socket = useRef<WebSocket | null>(null)
  const filter = useRef<LiveFilter | null>(null)
  const ticketRef = useRef(ticket)
  ticketRef.current = ticket

  // biome-ignore lint/correctness/useExhaustiveDependencies: `generation` reopens the view on reconnect
  useEffect(() => {
    if (!enabled) return
    let stopped = false
    let tries = 0
    let retry: ReturnType<typeof setTimeout> | undefined
    let poll: ReturnType<typeof setInterval> | undefined
    let steady: ReturnType<typeof setTimeout> | undefined
    const send = (m: unknown) => {
      if (socket.current?.readyState === WebSocket.OPEN) socket.current.send(JSON.stringify(m))
    }

    async function open() {
      dispatch({ type: 'connecting' })
      setProblem(null)
      let t: LiveTicket
      try {
        t = await ticketRef.current()
      } catch (error) {
        // Never retried here: a 429 waits for the viewer, after its Retry-After.
        if (stopped) return
        setProblem(problemOf(error))
        setRetryAt(waitUntil(error))
        return
      }
      if (stopped) return
      if (!sameOrigin(t.url, origin)) {
        setProblem('unavailable')
        return
      }
      const ws = new WebSocket(t.url)
      socket.current = ws
      ws.onopen = () => {
        dispatch({ type: 'open' })
        clearTimeout(steady)
        steady = setTimeout(() => {
          tries = 0
        }, STEADY_MS)
        // A filter set before a reconnect carries over.
        if (filter.current) send({ type: 'filter', ...filter.current })
        send({ type: 'status' })
        clearInterval(poll)
        poll = setInterval(() => send({ type: 'status' }), STATUS_EVERY_MS)
      }
      ws.onmessage = (e) => {
        if (typeof e.data !== 'string') return
        let data: unknown
        try {
          data = JSON.parse(e.data)
        } catch {
          return
        }
        const kind = (data as { type?: unknown } | null)?.type
        if (kind === 'status') tries = 0
        if (renewSent.current && (kind === 'renew' || kind === 'error')) {
          renewSent.current = false
          if (kind === 'error') {
            const said = (data as { error?: unknown }).error
            setRenewProblem('failed')
            setRenewRefused(typeof said === 'string' ? said.slice(0, 200) : null)
          }
        }
        dispatch({ type: 'message', data })
      }
      ws.onclose = (e) => {
        clearInterval(poll)
        clearTimeout(steady)
        renewSent.current = false
        if (socket.current === ws) socket.current = null
        if (stopped) return
        dispatch({ type: 'close', code: e.code })
        if (e.code === TICKET_EXPIRED) return
        const wait = RETRY_MS[tries]
        if (wait === undefined) {
          setProblem('failed')
          return
        }
        tries++
        retry = setTimeout(open, wait)
      }
    }

    open()
    return () => {
      stopped = true
      clearTimeout(retry)
      clearInterval(poll)
      clearTimeout(steady)
      socket.current?.close(1000)
      socket.current = null
    }
  }, [enabled, origin, generation])

  const setFilter = useCallback((f: LiveFilter) => {
    filter.current = f
    if (socket.current?.readyState === WebSocket.OPEN)
      socket.current.send(JSON.stringify({ type: 'filter', ...f }))
  }, [])

  const renew = useCallback(async () => {
    // Only an open view can be renewed: no ticket (an audit row) for one that cannot carry it.
    if (socket.current?.readyState !== WebSocket.OPEN) return
    setRenewing(true)
    setRenewProblem(null)
    setRenewRefused(null)
    try {
      const t = await ticketRef.current()
      if (socket.current?.readyState === WebSocket.OPEN) {
        renewSent.current = true
        socket.current.send(JSON.stringify({ type: 'renew', ticket: t.ticket }))
      }
    } catch (error) {
      setRenewProblem(problemOf(error))
      setRetryAt(waitUntil(error))
    } finally {
      setRenewing(false)
    }
  }, [])

  const reconnect = useCallback(() => setGeneration((g) => g + 1), [])

  return {
    view,
    problem,
    renewProblem,
    renewRefused,
    retryAt,
    setFilter,
    renew,
    reconnect,
    renewing,
  }
}

function sameOrigin(url: string, origin: string): boolean {
  try {
    return origin !== '' && new URL(url).origin === new URL(origin).origin
  } catch {
    return false
  }
}

/** The API's answer to a ticket, as the view explains it. */
function problemOf(error: unknown): LiveProblem {
  const status = (error as { status?: unknown } | null)?.status
  if (status === 503) return 'unavailable'
  if (status === 404) return 'not_tester'
  if (status === 403) return 'forbidden'
  if (status === 429) return 'rate_limited'
  return 'failed'
}

/** After a 429: when its Retry-After ends, Unix ms; null for any other answer. */
export function waitUntil(error: unknown, now = Date.now()): number | null {
  const e = error as { status?: unknown; retryAfter?: unknown } | null
  if (e?.status !== 429) return null
  const s = typeof e.retryAfter === 'number' && e.retryAfter > 0 ? e.retryAfter : RATE_LIMIT_WAIT_S
  return now + s * 1000
}
